import { events, orders as ordersApi, sameScope, NO_SCOPE, type StorefrontScope } from '../api/endpoints.ts';
import type { LocationEvent, NotificationEvent, OrderListItem, Order, StockEvent, StoreStatus } from '../api/types.ts';
import { OrderStatusGroup } from '../core/order-status.ts';
import { createStore, useStore } from '../core/store.ts';
import { applyStoreStatus, publishStock, refreshNotifications, refreshStoreStatus } from './stores.ts';

/**
 * Наблюдатель за заказами над всеми экранами: слушает ленту событий, превращает смену статуса
 * в баннер сверху и предлагает оценить вручённый заказ.
 *
 * Пока вкладка на экране, открыт поток `GET /v1/events`: событие заказа перечитывает заказы в
 * работе. При подключении и раз в две минуты (страховка от молча умершего потока) список
 * читается целиком. Уход вкладки в фон закрывает поток, возврат открывает его заново с полным
 * чтением; только что оформленный заказ (`pokeWatcher`) делает то же. Заказ, ушедший из
 * открытых, читается отдельно: его конечный статус и есть событие. Та же лента несёт остатки
 * склада адреса (`stock`), новые уведомления (`notification`), статус магазина (`store`),
 * позицию курьера (`location`) и сообщения поддержки (`support`).
 */
export type StatusNotice = { id: number; orderId: string; status: string };

/** Курьер у двери и у заказа есть код вручения: код открывается поверх любого экрана. */
export type CodePrompt = { orderId: string; orderNumber: string; code: string };

export type WatcherState = {
  /** Баннер о смене статуса: тап открывает заказ, сам гаснет через 6 секунд. */
  notice: StatusNotice | null;
  codePrompt: CodePrompt | null;
  /** Только что вручённый и ещё не оценённый заказ: поднимается лист оценки. */
  ratingPrompt: OrderListItem | null;
  /** Заказы в работе: полоса над вкладками. */
  active: OrderListItem[];
  /** Заказ, оценённый из всплывшего листа: открытый экран заказа перечитывает данные. */
  ratedOrderId: string | null;
  /** Растёт с каждым событием заказа: открытый экран заказа перечитывает заказ. */
  orderVersion: number;
  /** Последняя позиция курьера по заказу клиента. */
  lastLocation: LocationEvent | null;
  /** Растёт с каждым сообщением поддержки: открытая переписка перечитывается. */
  supportVersion: number;
};

export const watcherStore = createStore<WatcherState>({
  notice: null,
  codePrompt: null,
  ratingPrompt: null,
  active: [],
  ratedOrderId: null,
  orderVersion: 0,
  lastLocation: null,
  supportVersion: 0,
});

export function useWatcher(): WatcherState {
  return useStore(watcherStore);
}

const patch = (next: Partial<WatcherState>) => watcherStore.set((state) => ({ ...state, ...next }));

const FULL_SYNC_MS = 120_000;
const MAX_RECONNECT_MS = 30_000;
const NOTICE_MS = 6_000;

let scope: StorefrontScope = NO_SCOPE;
let signedIn = false;
/** Текущее соединение: полное чтение и поток. Существует, только пока вкладка на экране. */
let connection: AbortController | null = null;
let lastEventId: string | null = null;
/** Известные статусы. Первый снимок только запоминается: запуск — не событие. */
let known: Map<string, string> | null = null;
let noticeId = 0;
const ratedOrRefused = new Set<string>();

function listItem(order: Order): OrderListItem {
  return {
    id: order.id,
    number: order.number,
    status: order.status,
    placedAt: order.placedAt,
    addressText: order.addressText,
    itemsCount: order.items.reduce((sum, item) => sum + item.qty, 0),
    totalRsd: order.totalRsd,
    courierName: order.courierName,
    courierAvatarUrl: order.courierAvatarUrl,
    expectedAt: order.expectedAt,
  };
}

function showNotice(orderId: string, status: string): void {
  const id = ++noticeId;
  patch({ notice: { id, orderId, status } });
  setTimeout(() => {
    if (watcherStore.get().notice?.id === id) patch({ notice: null });
  }, NOTICE_MS);
}

async function tick(): Promise<void> {
  if (!signedIn) {
    patch({ active: [] });
    return;
  }
  const response = await ordersApi.list({ status: 'open', limit: 30 }).catch(() => null);
  if (!response) return;
  const items = [...response.items];

  if (known) {
    // Заказ, который был в работе и ушёл из открытых, завершился: его итог читается отдельно.
    const present = new Set(items.map((item) => item.id));
    for (const [id, old] of known) {
      if (present.has(id) || !OrderStatusGroup.isActive(old)) continue;
      const order = await ordersApi.get(id).catch(() => null);
      if (order) items.push(listItem(order));
    }

    let changed: OrderListItem | null = null;
    for (const item of items) {
      const old = known.get(item.id);
      if (old !== undefined && old !== item.status) changed = item;
      known.set(item.id, item.status);
    }
    if (changed) {
      showNotice(changed.id, changed.status);
      if (OrderStatusGroup.delivered.has(changed.status) && !ratedOrRefused.has(changed.id)) {
        patch({ ratingPrompt: changed });
      }
    }
  } else {
    known = new Map(items.map((item) => [item.id, item.status]));
  }

  patch({ active: items.filter((item) => OrderStatusGroup.isActive(item.status)) });
}

function parse<T>(data: string): T | null {
  try {
    return JSON.parse(data) as T;
  } catch {
    return null;
  }
}

/** Читает поток до конца. Рядом раз в две минуты идёт полное чтение. `true` — сервер закрыл сам. */
async function listen(signal: AbortSignal): Promise<boolean> {
  const fullSync = setInterval(() => void tick(), FULL_SYNC_MS);
  try {
    await events.listen(scope, lastEventId, signal, (frame) => {
      if (frame.id) lastEventId = frame.id;
      switch (frame.event) {
        case 'order':
          void tick().then(() => watcherStore.set((state) => ({ ...state, orderVersion: state.orderVersion + 1 })));
          break;
        case 'location': {
          const event = parse<LocationEvent>(frame.data);
          if (event) patch({ lastLocation: event });
          break;
        }
        case 'support':
          watcherStore.set((state) => ({ ...state, supportVersion: state.supportVersion + 1 }));
          break;
        case 'stock': {
          const event = parse<StockEvent>(frame.data);
          if (event) publishStock(event);
          break;
        }
        case 'notification': {
          const event = parse<NotificationEvent>(frame.data);
          if (event?.kind === 'delivery_code' && event.orderId) void promptCode(event.orderId);
          void refreshNotifications();
          break;
        }
        case 'store': {
          const status = parse<StoreStatus>(frame.data);
          if (status) applyStoreStatus(status);
          break;
        }
      }
    });
    return true;
  } catch {
    return false;
  } finally {
    clearInterval(fullSync);
  }
}

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      resolve();
    });
  });

async function loop(signal: AbortSignal): Promise<void> {
  let delay = 1_000;
  while (!signal.aborted) {
    await tick();
    if (signedIn) await refreshNotifications();
    await refreshStoreStatus(scope);
    if (signal.aborted) return;
    const closedCleanly = await listen(signal);
    if (signal.aborted) return;
    // Сервер сам закрывает поток раз в десять минут, это не сбой: переподключение сразу.
    // На ошибке пауза растёт до 30 секунд, чтобы не долбить лежащий сервер.
    delay = closedCleanly ? 1_000 : Math.min(delay * 2, MAX_RECONNECT_MS);
    await sleep(delay, signal);
  }
}

function resume(): void {
  if (connection) return;
  connection = new AbortController();
  void loop(connection.signal);
}

function suspend(): void {
  connection?.abort();
  connection = null;
}

/** Вкладка на экране: поток открыт. В фоне соединение не держится. */
export function setWatcherForeground(foreground: boolean): void {
  if (foreground) resume();
  else suspend();
}

/** Полное чтение и новый поток сразу: только что оформленный заказ, смена адреса или входа. */
export function pokeWatcher(): void {
  if (!connection) return;
  suspend();
  resume();
}

/** Адрес выбирает склад, чьи остатки несёт лента; смена переподключает её. */
export function setWatcherScope(next: StorefrontScope): void {
  if (sameScope(scope, next)) return;
  scope = next;
  pokeWatcher();
}

/** У гостя нет ни заказов, ни уведомлений: читаются только остатки и статус магазина. */
export function setWatcherSignedIn(next: boolean): void {
  if (signedIn === next) return;
  signedIn = next;
  known = null;
  pokeWatcher();
}

export function dismissNotice(): void {
  patch({ notice: null });
}

/** Курьер у двери открыл ввод кода (событие ленты или пуш): код читается из заказа и поднимается поверх экрана. */
export async function promptCode(orderId: string): Promise<void> {
  let order: Order;
  try {
    order = await ordersApi.get(orderId);
  } catch {
    return;
  }
  if (!order.deliveryCode || !order.requiresDeliveryCode || !OrderStatusGroup.onTheWay.has(order.status)) return;
  const current = watcherStore.get().codePrompt;
  if (current?.orderId === orderId && current.code === order.deliveryCode) return;
  patch({ codePrompt: { orderId, orderNumber: order.number, code: order.deliveryCode } });
}

export function dismissCodePrompt(): void {
  patch({ codePrompt: null });
}

export function dismissRating(rated: boolean): void {
  const prompt = watcherStore.get().ratingPrompt;
  if (prompt) {
    ratedOrRefused.add(prompt.id);
    patch({ ratingPrompt: null, ...(rated ? { ratedOrderId: prompt.id } : {}) });
  }
}

/** Заказ только что оформлен: запоминается, чтобы его первый статус не выглядел сменой. */
export function trackOrder(orderId: string, status: string): void {
  known?.set(orderId, status);
  pokeWatcher();
}
